import { __assign } from "tslib";
import * as React from 'react';
import { namespacedClassnames, isPromise } from '../../../../common';
import { keyCodes, classNames } from '@wix/answers-lib';
import { Text } from '../../typography';
import { ReactSelectAsync } from '../../../../common/react-select';
import { Icon } from '../../../../primitives/icon/icon';
import { smallCollapsibleToggle, checkGreen } from '../../../../icons';
import { InlineControl } from '../common/inline-control';
import { Label } from '../../labels';
import { LibColor } from '../../colors/colors-map';
import { TinyLoader } from '../../../../primitives/loaders/tiny-loader';
import { Tooltip } from '../../../../primitives';
import { isTextSelected, isValueEqualMultiSelect } from '../common/utils';
export var inlineMultiSelectKey = 'inline-multi-select';
var MIN_HEIGHT = 40;
var PADDING = 10;
var expandButtonHeight = 17;
export var InlineMultiSelect = function (props) {
    var _a;
    var _b, _c;
    var value = props.value, texts = props.texts, options = props.options, onChange = props.onChange, className = props.className, maxRows = props.maxRows, columnView = props.columnView, hideClear = props.hideClear, customViewRenderer = props.customViewRenderer, optionRenderer = props.optionRenderer, removeSelectedOptions = props.removeSelectedOptions;
    var placeholder = texts.placeholder, clear = texts.clear, unfocusedPlaceholder = texts.unfocusedPlaceholder, focusedPlaceholder = texts.focusedPlaceholder;
    var _d = React.useState(false), isFocused = _d[0], setIsFocused = _d[1];
    var _e = React.useState(false), isHovered = _e[0], setIsHovered = _e[1];
    var _f = React.useState(false), isExpanded = _f[0], setIsExpanded = _f[1];
    var _g = React.useState(value), unconfirmedValues = _g[0], setUnconfirmedValues = _g[1];
    var _h = React.useState(''), searchTerm = _h[0], setSearchTerm = _h[1];
    var _j = React.useState(false), isLoading = _j[0], setIsLoading = _j[1];
    var unfocusedBodyRef = React.useRef(null);
    var innerUnfocusedBodyRef = React.useRef(null);
    var unfocusedBodyHeight = (_b = unfocusedBodyRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height;
    var innerUnfocusedBodyHeight = (_c = innerUnfocusedBodyRef.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().height;
    var MAX_BOX_HEIGHT = maxRows ? maxRows * (MIN_HEIGHT - PADDING) + 2 * PADDING : 1000;
    var _k = React.useState(MIN_HEIGHT), valuesHeight = _k[0], setValuesHeight = _k[1];
    var heightTrigger = valuesHeight > MAX_BOX_HEIGHT;
    var _l = React.useState(false), noResults = _l[0], setNoResults = _l[1];
    React.useEffect(function () {
        if (!!unfocusedBodyRef.current && columnView) {
            setValuesHeight(unfocusedBodyHeight || 1000);
        }
        else if (!!innerUnfocusedBodyRef.current) {
            setValuesHeight(innerUnfocusedBodyHeight || 1000);
        }
    }, [isFocused, unfocusedBodyHeight, valuesHeight, innerUnfocusedBodyHeight]);
    React.useEffect(function () {
        var noResultsExists = !!document.getElementsByClassName('no-results-content').length;
        setNoResults(noResultsExists);
    }, [searchTerm]);
    React.useEffect(function () {
        setUnconfirmedValues(value);
    }, [isValuePropChanged()]);
    function isValuePropChanged() {
        return isValueEqualMultiSelect(usePrevious(value), value);
    }
    function usePrevious(val) {
        var ref = React.useRef(null);
        React.useEffect(function () {
            ref.current = val;
        });
        return ref.current;
    }
    var cn = namespacedClassnames(inlineMultiSelectKey, className, (_a = {
            minimized: heightTrigger && !isExpanded,
            focused: isFocused,
            'preserve-layout': props.preserveContentLayout
        },
        _a['hide-selected-option'] = !!removeSelectedOptions,
        _a));
    var onFocus = function () {
        var _a;
        if (!isTextSelected(window)) {
            setIsFocused(!props.disabled);
        }
        (_a = props.onFocus) === null || _a === void 0 ? void 0 : _a.call(props);
    };
    var onMouseOver = function () { return setIsHovered(true); };
    var onMouseLeave = function () { return setIsHovered(false); };
    var toggleExpand = function () { return setIsExpanded(!isExpanded); };
    var onLeaveFocus = function () {
        setIsLoading(false);
        setIsHovered(false);
        setIsFocused(false);
    };
    var fakeLoadOptions = function (_, cb) {
        cb(null, { options: options });
    };
    var onChangeSelected = function (selectedValue) {
        setUnconfirmedValues(selectedValue);
        setSearchTerm('');
    };
    var onConfirm = function (e) {
        e.preventDefault();
        var result = onChange(unconfirmedValues);
        if (isPromise(result)) {
            setIsLoading(true);
            result.then(onLeaveFocus, function () {
                return onCancel();
            });
        }
        else {
            onLeaveFocus();
        }
    };
    var onCancel = function (e) {
        if (e) {
            e.preventDefault();
        }
        setIsExpanded(false);
        onLeaveFocus();
        setUnconfirmedValues(value);
    };
    var onClear = function (e) {
        e.preventDefault();
        setUnconfirmedValues([]);
        setSearchTerm('');
    };
    var handleInputChange = function (input) {
        var onInputChange = props.onInputChange;
        if (onInputChange) {
            onInputChange(input);
        }
        setSearchTerm(input);
    };
    var onKeyDown = function (e) {
        e.nativeEvent.stopImmediatePropagation();
        var isMenuOpen = !!document.getElementsByClassName('Select is-open').length;
        if (!isMenuOpen) {
            switch (e.keyCode) {
                case (keyCodes.enter):
                    onConfirm(e);
                    break;
                case (keyCodes.escape):
                    onCancel(e);
                    break;
                default:
                    return;
            }
        }
    };
    var expandButton = (React.createElement("div", { className: 'expand-button' },
        React.createElement(Text, { onClick: toggleExpand, color: 't60', type: 'ts' }, isExpanded ? 'Show less' : 'Show more')));
    var focusedBody = function () {
        var _a;
        var defaultHeight = Math.max(valuesHeight || 0, MIN_HEIGHT);
        var columnViewHeight = Math.max(Math.min(!!(unconfirmedValues === null || unconfirmedValues === void 0 ? void 0 : unconfirmedValues.length) ? valuesHeight + PADDING : valuesHeight, heightTrigger ? MAX_BOX_HEIGHT + expandButtonHeight : MAX_BOX_HEIGHT + 2), MIN_HEIGHT);
        var componentCalculateHeight = columnView ? columnViewHeight : defaultHeight;
        var componentStyle = (_a = {
                height: props.preserveContentLayout ? undefined : (componentCalculateHeight)
            },
            _a['--var-query-width'] = (searchTerm.length * 8 || 1) + 'px',
            _a);
        var noResultsContent = (React.createElement("div", { className: 'no-results-content' },
            React.createElement("div", { className: 'primary-text' },
                React.createElement(Text, { className: 'primary-texts' }, 'No results matching'),
                React.createElement(Text, { className: 'primary-texts', type: 'tm' }, "'" + searchTerm + "'")),
            React.createElement(Text, { type: 'ts', color: 't40' }, 'Try searching for another term')));
        var defaultValueRenderer = function (option) {
            var onRemove = function (e) {
                e.preventDefault();
                e.nativeEvent.stopImmediatePropagation();
                option.onRemove(option.value);
            };
            var color = option.isLoading ? LibColor.grey190 : option.value.color || LibColor.sky120;
            var body = props.focusedValueRenderer ? (props.focusedValueRenderer(option.value)) : (React.createElement(Text, { className: 'label-value', ellipsis: true, relativeToBody: true }, option.value.label));
            return (React.createElement(Label, { className: 'inline-multi-value', color: color, onRemove: onRemove }, body));
        };
        var innerOptionRenderer = function (option) {
            return (React.createElement("div", { className: 'rendered-option', "data-value": option.value },
                React.createElement("div", { className: 'option-label' }, option.label),
                React.createElement(Icon, { icon: checkGreen, className: 'check-icon' })));
        };
        var inlineControl = (React.createElement(InlineControl, { onConfirm: onConfirm, onCancel: onCancel, onClear: onClear, disabledClear: !(unconfirmedValues === null || unconfirmedValues === void 0 ? void 0 : unconfirmedValues.length) && !searchTerm, disabledConfirm: noResults, clearText: clear, hideClear: hideClear }));
        var loader = React.createElement("div", { className: 'loader-wrapper' },
            React.createElement(TinyLoader, null));
        var toggleIcon = React.createElement(Icon, { icon: smallCollapsibleToggle, className: 'toggle-icon' });
        //temp solution for hiding empty menu when all options selected; in select v3 will work with hideSelectedOptions & closeMenuOnSelect
        var allSelectedHideMenu = removeSelectedOptions && (unconfirmedValues === null || unconfirmedValues === void 0 ? void 0 : unconfirmedValues.length) === options.length;
        var allSelectedHideMenuStyle = allSelectedHideMenu ? { display: 'none' } : undefined;
        return (React.createElement("div", { className: 'focused-body', style: componentStyle },
            React.createElement("div", { className: classNames('select-container', { 'hide-menu': allSelectedHideMenu }) },
                React.createElement(ReactSelectAsync, __assign({}, props, { openOnFocus: true, valueComponent: defaultValueRenderer, multi: true, loadOptions: fakeLoadOptions, placeholder: focusedPlaceholder || placeholder, onSelectResetsInput: true, onBlurResetsInput: true, onChange: onChangeSelected, inputValue: searchTerm, onInputChange: handleInputChange, value: unconfirmedValues, clearable: false, onInputKeyDown: onKeyDown, noResultsText: noResultsContent, autosize: false, removeSelected: false, optionRenderer: optionRenderer || innerOptionRenderer, closeOnSelect: false, autoFocus: true, disabled: isLoading, filterOption: props.onSearchFilter, menuContainerStyle: allSelectedHideMenuStyle })),
                isLoading ? loader : toggleIcon),
            isLoading || (searchTerm && !noResults) ? null : inlineControl));
    };
    var renderUnfocusedValue = function (selectValue) {
        if (props.unfocusedValueRenderer) {
            return React.createElement("div", null,
                " ",
                (props.unfocusedValueRenderer(selectValue)),
                " ");
        }
        return selectValue.label;
    };
    var unFocusedBody = function () {
        var _a;
        var style = (_a = {}, _a['--var-max-height'] = (MAX_BOX_HEIGHT || 0) + "px", _a.paddingBottom = columnView ? 0 : 8, _a);
        var maybeHover = (isHovered && !props.disabled) ?
            React.createElement(Text, { color: 't60' }, unfocusedPlaceholder ? unfocusedPlaceholder : placeholder) :
            React.createElement(Text, { className: 'unfocused-placeholder', color: 't50' }, unfocusedPlaceholder ? unfocusedPlaceholder : '-');
        var values = value && (value === null || value === void 0 ? void 0 : value.length) ? value.map(function (val, idx) {
            var inlineValueStyle = {
                visibility: maxRows && !isExpanded && idx + 1 > maxRows ? 'hidden' : 'visible',
                marginTop: typeof val.label === typeof '' ? 1 : 0,
            };
            return (React.createElement("div", { className: 'inline-value', key: idx, style: inlineValueStyle }, val.label));
        }) : maybeHover;
        var labelStyle = { marginTop: 1, height: 'fit-content' };
        var labels = value && value.length ? (props.unfocusedValueRenderer ?
            React.createElement("div", { className: 'inline-value', style: labelStyle }, value.map(renderUnfocusedValue)) :
            React.createElement("div", { className: 'inline-value', style: labelStyle }, value.map(function (val) { return val.label; }).join(', '))) : maybeHover;
        var comp = (React.createElement("div", { style: style, ref: innerUnfocusedBodyRef, className: classNames('unfocused-body', { disabled: !!props.disabled }) }, value ? React.createElement("div", { ref: unfocusedBodyRef }, columnView ? values : labels) : maybeHover));
        if (props.disabled) {
            return (React.createElement(Tooltip, { relativeToBody: true, body: props.disabled }, comp));
        }
        else if (customViewRenderer) {
            return React.createElement("div", { className: 'custom-view' }, customViewRenderer());
        }
        else {
            return comp;
        }
    };
    var fieldContent = props.preserveContentLayout ? (React.createElement(React.Fragment, null,
        unFocusedBody(),
        isFocused && focusedBody())) : (isFocused ? focusedBody() : unFocusedBody());
    return (React.createElement("div", { className: cn },
        React.createElement("div", { onClick: onFocus, onBlur: onConfirm, onMouseOver: onMouseOver, onMouseLeave: onMouseLeave }, fieldContent),
        !isFocused && heightTrigger ? expandButton : null));
};
//# sourceMappingURL=index.js.map